import { appointmentStatusMeta, formatPrice, formatTime } from "@coucou/ui";
import { SALON_TIMEZONE as TZ } from "../lib/trpc";
import { minutesFromDayStart, shiftDayKey, todayKey, zonedStartOfDay } from "../lib/time";

/**
 * Vues semaine et mois.
 *
 * Elles répondent à des questions différentes de la vue jour, et c'est ce qui
 * dicte leur forme :
 *
 *  - **Jour** : « que fait chaque employée, minute par minute ? » → colonnes
 *    par employée, échelle temporelle fine.
 *  - **Semaine** : « où sont les trous, et qui est chargé ? » → colonnes par
 *    jour, toutes employées confondues, couleur pour distinguer.
 *  - **Mois** : « quand suis-je pleine, quand suis-je vide ? » → densité, pas
 *    de détail. Afficher chaque rendez-vous y serait illisible.
 */

export type PlanningAppointment = {
  id: string;
  staffId: string;
  status: keyof typeof appointmentStatusMeta;
  startsAt: Date;
  endsAt: Date;
  blockStartsAt: Date;
  blockEndsAt: Date;
  totalPriceCents: number;
  guestName: string | null;
  client: { firstName: string | null } | null;
  lines: { nameSnapshot: string }[];
};

export type StaffColumn = { id: string; displayName: string; calendarColor: string };

const PX_PER_MINUTE = 0.8;
const WEEK_RANGE = { from: 8 * 60, to: 20 * 60 };

const clientNameOf = (appointment: PlanningAppointment) =>
  appointment.client?.firstName ?? appointment.guestName ?? "Cliente";

/** Lundi de la semaine contenant `dateKey`. */
export function startOfWeek(dateKey: string): string {
  const date = new Date(`${dateKey}T12:00:00Z`);
  // getUTCDay : 0 = dimanche. On ramène au lundi précédent.
  const offset = (date.getUTCDay() + 6) % 7;
  return shiftDayKey(dateKey, -offset);
}

export function startOfMonth(dateKey: string): string {
  return `${dateKey.slice(0, 7)}-01`;
}

/* -------------------------------------------------------------------------- */
/*  Semaine                                                                    */
/* -------------------------------------------------------------------------- */

export function WeekView({
  dateKey,
  staff,
  appointments,
  onSelect,
  onCreate,
}: {
  dateKey: string;
  staff: StaffColumn[];
  appointments: PlanningAppointment[];
  onSelect: (id: string) => void;
  onCreate: (blockStartsAt: Date) => void;
}) {
  const monday = startOfWeek(dateKey);
  const days = Array.from({ length: 7 }, (_, index) => shiftDayKey(monday, index));
  const height = (WEEK_RANGE.to - WEEK_RANGE.from) * PX_PER_MINUTE;
  const today = todayKey(TZ);

  return (
    <div className="min-w-0 flex-1 overflow-auto rounded-xl bg-white shadow-soft">
      <div
        className="sticky top-0 z-10 grid border-b border-creme-200 bg-white/95 backdrop-blur"
        style={{ gridTemplateColumns: `3.5rem repeat(7, minmax(8rem, 1fr))` }}
      >
        <div />
        {days.map((day) => (
          <div
            key={day}
            className={[
              "px-2 py-2 text-sm",
              day === today ? "font-medium text-blush-600" : "text-encre-700",
            ].join(" ")}
          >
            {new Intl.DateTimeFormat("fr-FR", {
              timeZone: TZ,
              weekday: "short",
              day: "numeric",
            }).format(new Date(`${day}T12:00:00Z`))}
          </div>
        ))}
      </div>

      <div
        className="relative grid"
        style={{
          gridTemplateColumns: `3.5rem repeat(7, minmax(8rem, 1fr))`,
          height: height + 12,
          paddingTop: 12,
        }}
      >
        <div className="relative border-r border-creme-200">
          {hoursIn(WEEK_RANGE).map((minute) => (
            <span
              key={minute}
              className="absolute right-2 -translate-y-1/2 text-xs tabular-nums text-encre-400"
              style={{ top: (minute - WEEK_RANGE.from) * PX_PER_MINUTE }}
            >
              {String(Math.floor(minute / 60)).padStart(2, "0")}h
            </span>
          ))}
        </div>

        {days.map((day) => {
          const dayStart = zonedStartOfDay(day, TZ);
          const ofDay = appointments.filter(
            (appointment) => keyOf(appointment.blockStartsAt) === day,
          );

          return (
            <div
              key={day}
              className="relative border-r border-creme-100 last:border-r-0"
              /*
                Clic sur une zone libre : créer un rendez-vous à cette heure.
                C'était un double-clic — geste que rien n'annonçait, et que
                personne ne tentait. Les cartes sont des `button` : on les
                laisse ouvrir le rendez-vous plutôt que d'en créer un autre.

                Les colonnes sont des éléments de grille : leur bord haut tombe
                DÉJÀ sous le `paddingTop` du conteneur. L'ancien `- 12` le
                retranchait une seconde fois et décalait la création d'un quart
                d'heure vers le haut.
              */
              onClick={(event) => {
                if ((event.target as HTMLElement).closest("button")) return;
                const bounds = event.currentTarget.getBoundingClientRect();
                const minutes = WEEK_RANGE.from + (event.clientY - bounds.top) / PX_PER_MINUTE;
                const snapped = Math.min(
                  WEEK_RANGE.to - 15,
                  Math.max(WEEK_RANGE.from, Math.round(minutes / 15) * 15),
                );
                onCreate(new Date(dayStart.getTime() + snapped * 60_000));
              }}
            >
              {hoursIn(WEEK_RANGE).map((minute) => (
                <div
                  key={minute}
                  className="absolute inset-x-0 border-t border-creme-100"
                  style={{ top: (minute - WEEK_RANGE.from) * PX_PER_MINUTE }}
                />
              ))}

              {ofDay.map((appointment) => {
                const member = staff.find((entry) => entry.id === appointment.staffId);
                const top = minutesFromDayStart(appointment.blockStartsAt, dayStart) - WEEK_RANGE.from;
                const duration =
                  (appointment.blockEndsAt.getTime() - appointment.blockStartsAt.getTime()) / 60_000;
                const meta = appointmentStatusMeta[appointment.status];

                return (
                  <button
                    key={appointment.id}
                    type="button"
                    onClick={() => onSelect(appointment.id)}
                    className={[
                      "absolute inset-x-0.5 overflow-hidden rounded px-1.5 py-0.5 text-left",
                      "transition-shadow hover:shadow-lift",
                      meta.blocking ? "" : "opacity-50 line-through",
                    ].join(" ")}
                    style={{
                      top: top * PX_PER_MINUTE,
                      height: Math.max(duration * PX_PER_MINUTE - 2, 18),
                      backgroundColor: `${member?.calendarColor ?? "#e88aa0"}22`,
                      borderLeft: `3px solid ${member?.calendarColor ?? "#e88aa0"}`,
                    }}
                  >
                    <span className="block truncate text-[11px] font-medium tabular-nums text-encre-800">
                      {formatTime(appointment.startsAt, TZ)} {clientNameOf(appointment)}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Mois                                                                       */
/* -------------------------------------------------------------------------- */

export function MonthView({
  dateKey,
  appointments,
  absences,
  onPickDay,
}: {
  dateKey: string;
  appointments: PlanningAppointment[];
  absences: { staffId: string | null; startsAt: Date; endsAt: Date; type: string }[];
  onPickDay: (dateKey: string) => void;
}) {
  const first = startOfMonth(dateKey);
  const gridStart = startOfWeek(first);
  const month = dateKey.slice(0, 7);
  const today = todayKey(TZ);

  // Six semaines couvrent tous les cas de figure d'un mois.
  const cells = Array.from({ length: 42 }, (_, index) => shiftDayKey(gridStart, index));

  const byDay = new Map<string, PlanningAppointment[]>();
  for (const appointment of appointments) {
    const key = keyOf(appointment.blockStartsAt);
    byDay.set(key, [...(byDay.get(key) ?? []), appointment]);
  }

  const closedDays = new Set<string>();
  for (const absence of absences) {
    if (absence.staffId !== null) continue; // fermeture du salon uniquement
    const cursor = new Date(absence.startsAt);
    for (let guard = 0; cursor <= absence.endsAt && guard < 62; guard++) {
      closedDays.add(keyOf(cursor));
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
  }

  return (
    <div className="min-w-0 flex-1 overflow-auto rounded-xl bg-white p-3 shadow-soft">
      <div className="grid grid-cols-7 gap-1">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((label) => (
          <div key={label} className="px-2 py-1 text-xs font-medium text-encre-400">
            {label}
          </div>
        ))}

        {cells.map((day) => {
          const items = byDay.get(day) ?? [];
          const revenue = items.reduce((sum, item) => sum + item.totalPriceCents, 0);
          const outside = !day.startsWith(month);
          const closed = closedDays.has(day);

          return (
            <button
              key={day}
              type="button"
              onClick={() => onPickDay(day)}
              className={[
                "min-h-24 rounded-lg p-2 text-left align-top transition-colors",
                outside ? "opacity-40" : "",
                closed ? "bg-creme-200" : "bg-creme-50 hover:bg-creme-100",
                day === today ? "ring-2 ring-blush-400" : "",
              ].join(" ")}
            >
              <span
                className={[
                  "text-sm tabular-nums",
                  day === today ? "font-medium text-blush-600" : "text-encre-600",
                ].join(" ")}
              >
                {Number(day.slice(8, 10))}
              </span>

              {closed && <span className="mt-1 block text-[11px] text-encre-500">Fermé</span>}

              {items.length > 0 && (
                <>
                  {/*
                    Densité plutôt que détail : un mois affichant chaque
                    rendez-vous devient illisible. On lit la charge, on clique
                    pour le détail.
                  */}
                  <span className="mt-1 block text-[11px] font-medium text-encre-800">
                    {items.length} RDV
                  </span>
                  <span className="block text-[11px] tabular-nums text-encre-500">
                    {formatPrice(revenue)}
                  </span>
                  <span className="mt-1 flex gap-0.5">
                    {items.slice(0, 8).map((item) => (
                      <span
                        key={item.id}
                        aria-hidden
                        className="h-1 flex-1 rounded-full bg-blush-400"
                      />
                    ))}
                  </span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function hoursIn(range: { from: number; to: number }): number[] {
  const hours: number[] = [];
  for (let minute = Math.ceil(range.from / 60) * 60; minute <= range.to; minute += 60) {
    hours.push(minute);
  }
  return hours;
}

/** Clé calendaire d'un instant, dans le fuseau du salon. */
function keyOf(instant: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instant);
}
